import { CompactDealResponse, DealStatus, HasDeal, SortByOption } from '@business/entities/deal';
import { R } from '@core/utils/r';
import { CommonService } from '../common_service';

export function getStatusTitle(status: DealStatus) {
  switch (status) {
    case DealStatus.inProgress:
      return 'In Progress';
    case DealStatus.won:
      return 'Won';
    case DealStatus.lost:
      return 'Lost';
    case DealStatus.deleted:
      return 'Deleted';
    case DealStatus.reopen:
      return 'Reopen';
  }
  return 'Unknown';
}

export function getStatusEmoji(status: DealStatus) {
  switch (status) {
    case DealStatus.inProgress:
      return 'meh';
    case DealStatus.won:
      return 'smile';
    case DealStatus.lost:
      return 'frown';
    case DealStatus.deleted:
      return 'frown';
  }
  return 'Unknown';
}

export function getStatusColor(status: DealStatus) {
  switch (status) {
    case DealStatus.inProgress:
      return 'text-dark';
    case DealStatus.won:
      return 'text-success';
    case DealStatus.lost:
      return 'text-danger';
    case DealStatus.deleted:
      return 'text-danger';
  }
  return 'Unknown';
}

export function allStatuses() {
  return [DealStatus.inProgress, DealStatus.won, DealStatus.lost, DealStatus.deleted];
}

export function allSortByOptions(isGCInternalSchedularEnabled) {
  const options = [
    SortByOption.default,
    SortByOption.created_date_new_to_old,
    SortByOption.created_date_old_to_new,
    SortByOption.last_activity_new_to_old,
    SortByOption.last_activity_old_to_new,
    SortByOption.value_high_to_low,
    SortByOption.value_low_to_high,
  ];

  if (!isGCInternalSchedularEnabled) {
    return options;
  }

  options.push(SortByOption.event_new_to_old);
  options.push(SortByOption.event_old_to_new);

  return options;
}

export function getHasDealTitle(hasDeal: HasDeal) {
  switch (hasDeal) {
    case HasDeal.all:
      return 'All';
    case HasDeal.yes:
      return 'Has Deal';
    case HasDeal.no:
      return 'No Deal';
  }
}

export function getDealSortByOptionTitle(option: SortByOption) {
  switch (option) {
    case SortByOption.default:
      return 'Sort By: Default';
    case SortByOption.created_date_new_to_old:
      return 'Sort By: Created Date: New to Old';
    case SortByOption.created_date_old_to_new:
      return 'Sort By: Created Date: Old to New';
    case SortByOption.last_activity_new_to_old:
      return 'Sort By: Last Activity: Old to New';
    case SortByOption.last_activity_old_to_new:
      return 'Sort By: Last Activity: New to Old';
    case SortByOption.event_new_to_old:
      return 'Sort By: Event Date: New to Old';
    case SortByOption.event_old_to_new:
      return 'Sort By: Event Date: Old to New';
    case SortByOption.value_high_to_low:
      return 'Sort By: Value: Highest to Lowest';
    case SortByOption.value_low_to_high:
      return 'Sort By: Value: Lowest to Highest';
  }
}

/**
 * Orders a stage's deals for display. Purely a view concern — the caller never persists
 * this order (DealController::postOrder only writes menu_order for SortByOption.default).
 *
 * Deals flagged noValue are pinned to the bottom in both value directions, so the top of
 * a column stays meaningful whichever way it's sorted. R.sortBy is stable, so deals that
 * tie keep the order they arrived in.
 */
export function sortDealsBy<
  T extends Pick<CompactDealResponse, 'created' | 'lastActivity' | 'menuOrder' | 'noValue' | 'upcomingEventDate' | 'valueDefault'>,
>(deals: T[], option: SortByOption): T[] {
  switch (option) {
    case SortByOption.created_date_new_to_old:
      return R.sortBy(deals, d => -d.created);
    case SortByOption.created_date_old_to_new:
      return R.sortBy(deals, d => d.created);
    case SortByOption.last_activity_new_to_old:
      return R.sortBy(deals, d => d.lastActivity);
    case SortByOption.last_activity_old_to_new:
      return R.sortBy(deals, d => -d.lastActivity);
    case SortByOption.event_new_to_old:
      return R.sortBy(deals, d => d.upcomingEventDate);
    case SortByOption.event_old_to_new:
      return R.sortBy(deals, d => -d.upcomingEventDate);
    case SortByOption.value_high_to_low:
      return R.sortBy(deals, [d => (d.noValue ? 1 : 0), d => -(d.valueDefault || 0)]);
    case SortByOption.value_low_to_high:
      return R.sortBy(deals, [d => (d.noValue ? 1 : 0), d => d.valueDefault || 0]);
    default:
      return R.sortBy(deals, d => d.menuOrder);
  }
}
